import React, {
  CSSProperties,
  ReactElement,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Dropdown,
  DropdownProps,
  Form,
  FormProps,
  Icon,
  Input,
  InputProps,
} from "semantic-ui-react";

import moment from "moment";

import DateTextBox from "react-lib/apps/common/DateTextBox";
import TimeComboBox from "react-lib/apps/common/TimeComboBox";
import TreeViewSearchBox, { TreeViewSearchBoxProps } from "react-lib/apps/common/TreeViewSearchBox";

import DropdownOptions, { DropdownOptionsProps } from "./DropdownOptions";
import SearchBoxDropdown, {
  SearchBoxDropdownProps,
  SelectedItemHandler,
} from "./SearchBoxDropdown";

// Utils
import { formatDatetime } from "react-lib/utils/dateUtils";

// CSS
import "./ForceDropdownPointing.scss"

// Types
type EditorBoxColumnProps<T extends BoxTypes> = ValueProps<T> & {
  autoFocus?: boolean;
  boxType: T;
  disableAutoBlur?: boolean;
  disabled?: boolean;
  editing?: boolean;
  index?: number;
  name?: number | string;
  size?: FormProps["size"];
  dropdownLoading?: boolean;
  // callback
  onChange?: ChangeHandler<T>;
};

export type ChangeHandler<T = unknown> = (
  event: SyntheticEvent | null,
  data: {
    action?: string;
    data: Record<string, any> | null;
    index?: number;
    name?: number | string;
    value: ValueProps<T>["value"];
  }
) => void;

type OnChangeHandler = (
  e: SyntheticEvent | null,
  data: { action?: string; data?: Record<string, any> | null; value: number | string | null },
  isRefresh?: boolean
) => void;

type RefHandler = (
  ref:
    | HTMLDivElement
    | {
        ref?: {
          inputRef?: {
            current: HTMLInputElement | null;
          };
        };
      }
    | null
) => void;

type BoxTypes =
  | "date"
  | "datetime"
  | "dropdown"
  | "dropdown-options"
  | "input"
  | "searchbox"
  | "treeview";

type CommonExcludedProps = {
  onEvent?: never;
  defaultText?: never;
  type?: never;
  valueKey?: never;
  searchedItemListWithKey?: never;
  mapOptions?: never;
  onBlur?: never;
  onBlurInput?: never;
};

type CommonNeverProps = {
  id?: never;
  items?: never;
  menuStyle?: never;
  multiple?: never;
  options?: never;
  showSubOptions?: never;
  onParentSearch?: never;
  onSearch?: never;
  placeholder?: never;
  icon?: never;
  pointing?: never;
  dropdownStyles?: never;
};

type ValueProps<T> = T extends "dropdown"
  ? CommonExcludedProps &
      Omit<CommonNeverProps, "multiple" | "options" | "placeholder"> & {
        multiple?: boolean;
        value?: number | string;
        options?: DropdownProps["options"];
        placeholder?: DropdownProps["placeholder"];
        icon?: DropdownProps["icon"];
        pointing?: DropdownProps["pointing"];
        dropdownStyles?: DropdownProps["styles"];
      }
  : T extends "dropdown-options"
  ? CommonExcludedProps &
      Omit<CommonNeverProps, "menuStyle" | "multiple" | "options" | "showSubOptions"> &
      Pick<DropdownOptionsProps, "menuStyle" | "options" | "showSubOptions" | "value"> & {
        multiple?: boolean;
      }
  : T extends "searchbox"
  ? Omit<CommonNeverProps, "id"> &
      Pick<
        SearchBoxDropdownProps,
        | "defaultText"
        | "mapOptions"
        | "onBlur"
        | "onBlurInput"
        | "searchedItemListWithKey"
        | "type"
        | "valueKey"
      > & {
        onEvent: (e: any) => void;
        id: string;
        value?: number | string | null;
      }
  : T extends "treeview"
  ? CommonExcludedProps &
      Omit<CommonNeverProps, "id" | "items" | "onParentSearch" | "onSearch"> &
      Pick<TreeViewSearchBoxProps, "id" | "items" | "onParentSearch" | "onSearch"> & {
        value?: string;
      }
  : CommonExcludedProps &
      CommonNeverProps & {
        value?: string;
      };

type Direction = "Left" | "Top";

type StyleKeys =
  | "content"
  | "dateTextbox"
  | "dateTextboxInput"
  | "dateTimeTextboxInput"
  | "dropdown"
  | "icon"
  | "input"
  | "popup"
  | "searchbox"
  | "searchboxDropdown";

type Styles = Record<StyleKeys, CSSProperties>;

// Const
const HEIGHT = 40;

const MARGIN = 7;

// #const BG_COLOR = "rgb(255, 255, 204)";

const styles: Styles = {
  dateTextbox: { width: "100%" },
  dateTextboxInput: { width: "100%" },
  dateTimeTextboxInput: { width: "97px" },
  dropdown: { fontSize: "0.96em", width: "100%" },
  icon: { width: "82px" },
  input: { fontSize: "0.96em" },
  popup: {
    height: "fit-content",
    marginTop: -HEIGHT,
    width: "100%",
  },
  searchbox: { width: "100%" },
  searchboxDropdown: { fontSize: "0.96em" },
  content: {
    height: "calc(100% + 7px)",
    marginLeft: -4,
    width: "calc(100% - -10px)",
  },
};

const TIME_PATTERN = /\d+:\d+$/;

const EditorBoxColumn = (props: EditorBoxColumnProps<BoxTypes>) => {
  const [isRefresh, setIsRefresh] = useState<boolean>(false);

  // Ref
  const divRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const tbodyRef = useRef<HTMLDivElement | null>(null);
  const rtTableRef = useRef<HTMLDivElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);

  const isSearchBoxChanged = useRef<boolean>(false);
  const isDatetimeChanged = useRef<boolean>(false);

  const isOpen = useRef<boolean>(false);
  const isMounted = useRef<boolean>(false);
  const isOpenTime = useRef<boolean>(false);
  const isOpenDate = useRef<boolean>(false);

  const rtTdBg = useRef<string>("");

  // Callback effect
  const handleSetStyle = useCallback(() => {
    if (!inputRef.current) {
      return;
    }

    // ค้นหา rt-td ที่ใกล้ที่สุดของ inputRef เพื่อเซ็ตสีพื้นหลัง
    const rtTd = inputRef.current.closest(".rt-td,.editor-box-cell") as HTMLDivElement | null;

    // #rtTd.style.backgroundColor = BG_COLOR;

    // กำหนดเลือกคำสั่งเพื่อเปลี่ยนแปลงสไตล์ของ element ที่ใกล้ที่สุดของ inputRef ตาม selectors ที่กำหนด
    const selectors = [
      ".DayPickerInput",
      "[id*='SearchBoxDropdown']",
      ".ui.dropdown",
      ".ui.input",
      ".MuiAutocomplete-root",
    ];

    // ค้นหา element ที่ใกล้ที่สุดของ inputRef จาก selectors ที่กำหนด
    const closestElement = selectors.flatMap((selector) => {
      const elm = inputRef.current?.closest(selector);

      return elm ? [elm] : [];
    })[0] as HTMLElement | null;

    // ปรับขนาดความกว้างของ element ที่ใกล้ที่สุดของ inputRef ตามขนาดของ rt-td
    if (rtTd && closestElement && props.boxType !== "datetime") {
      const margin = rtTd.className === "rt-td" ? -12 : 0;

      closestElement.style.width = `${rtTd.offsetWidth + margin}px`;
    }

    handleScrollTop({ target: tbodyRef.current } as Event);
    handleScrollLeft({ target: rtTableRef.current } as Event);
  }, [props.boxType]);

  // Effect
  useEffect(() => {
    let rafId: number | undefined;

    const resizeObserver = new ResizeObserver(() => {
      try {
        if (rafId) {
          cancelAnimationFrame(rafId);
        }

        rafId = requestAnimationFrame(() => {
          handleSetStyle();
        });
      } catch (error) {
        console.error("ResizeObserver error:", error);
      }
    });

    if (inputRef.current) {
      resizeObserver.observe(inputRef.current);

      if (inputRef.current.parentElement) {
        resizeObserver.observe(inputRef.current.parentElement);
      }
    } else {
      console.error("Element to observe not found");
    }

    return () => {
      resizeObserver.disconnect();

      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [handleSetStyle]);

  // Callback
  const handleScroll = useCallback((e: any, direction: Direction) => {
    if (!(popupRef.current && isOpen.current && inputRef.current)) {
      return;
    }

    const target = e.target as HTMLDivElement;
    const dimension = `margin${direction}` as `margin${Direction}`;
    // คำนวณค่า scrollValue โดยดูทิศทางการ scroll (Top/Left) เพื่อปรับตำแหน่งของ popup
    const scrollValue =
      direction === "Top" ? -target.scrollTop + -HEIGHT + MARGIN : -target.scrollLeft;

    // ปรับตำแหน่งของ popup ตาม scrollValue ที่คำนวณได้
    popupRef.current.style[dimension] = `${scrollValue}px`;

    const {
      bottom: tableBottom,
      left: tableLeft,
      right: tableRight,
      top: tableTop,
    } = target.getBoundingClientRect();
    const { bottom, left, right, top } = inputRef.current.getBoundingClientRect();

    // ตรวจสอบว่าต้องการซ่อน popup หรือไม่ เมื่อ scroll ทิศทางเป็น Top หรือ Left
    const shouldBlurTop = direction === "Top" && (tableTop > top || tableBottom < bottom);
    const shouldBlurLeft = direction === "Left" && (tableLeft > left || tableRight < right);

    // ซ่อน popup
    if ((shouldBlurTop || shouldBlurLeft) && !props.disableAutoBlur) {
      void handleBlur();
    }
  }, []);

  const handleScrollTop = useCallback((e: Event) => {
    handleScroll(e, "Top");
  }, []);

  const handleScrollLeft = useCallback((e: Event) => {
    handleScroll(e, "Left");
  }, []);

  const handleEventListener = useCallback(() => {
    const input = inputRef.current;

    if (input) {
      // เซ็ตค่า isSearchBoxChanged เป็น false เมื่อมีการ focus ที่ input
      isSearchBoxChanged.current = false;
      isDatetimeChanged.current = false;

      // เพิ่ม event listener เมื่อมีการ focus ที่ input
      input.onfocus = () => {
        handleClick(); // เรียกใช้ handleClick เมื่อมีการ focus ที่ input
        handleEventListener(); // เรียกใช้ฟังก์ชัน handleEventListener เพื่อให้กำหนด event listener ใหม่หลังจาก focus
      };

      input.onblur = (e) => {
        const picker = input.closest(".DayPickerInput");

        setTimeout(() => {
          // ตรวจสอบว่า element ที่ blur ไปอยู่ใน picker หรือไม่
          const isPickerValid = picker?.contains(e.target as Node) && !isDatetimeChanged.current;

          if (isPickerValid && props.boxType !== "date") {
            return;
          }

          const shouldFocusTime = isOpenTime.current && picker;
          const shouldFocusDate = isOpenDate.current && input.closest(".DropdownOptions");
          const isMultipleDropdown = props.boxType === "dropdown-options" && props.multiple;

          // Exit if any condition requires maintaining focus
          if (shouldFocusTime || shouldFocusDate || isMultipleDropdown) {
            return;
          }

          handleDelayBlur();
        }, 150);
      };
    }

    const tbody = tbodyRef.current;
    const rtTable = rtTableRef.current;

    // ตรวจสอบและเพิ่มหรือลบ event listener เมื่อมีการ scroll ที่ tbody และ rtTable
    if (tbody) {
      tbody.removeEventListener("scroll", handleScrollTop);
      tbody.addEventListener("scroll", handleScrollTop);
    }

    if (rtTable) {
      rtTable.removeEventListener("scroll", handleScrollLeft);
      rtTable.addEventListener("scroll", handleScrollLeft);
    }
  }, [props.boxType]);

  const handleRef: RefHandler = useCallback(
    (ref) => {
      if (!ref) {
        return;
      }

      inputRef.current =
        ref instanceof HTMLDivElement
          ? ref.querySelector(".menu input") || ref.querySelector("input")
          : ref.ref?.inputRef?.current || null;

      const rtTbody = inputRef.current?.closest(".rt-tbody,.editor-box-body");

      tbodyRef.current = rtTbody as HTMLDivElement | null;
      rtTableRef.current = tbodyRef.current?.parentElement as HTMLDivElement;

      handleSetStyle();

      handleEventListener();

      if (props.autoFocus) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 150);
      }
    },
    [handleEventListener, handleSetStyle, props.autoFocus]
  );

  const handleLabelRef = useCallback(
    (ref: HTMLSpanElement | null) => {
      if (!ref) {
        return;
      }

      const rtTd = ref.closest(".rt-td,.editor-box-cell") as HTMLDivElement | null;

      if (!rtTd) {
        return;
      }

      // ตรวจสอบว่า component ถูก mount หรือยัง และกำหนดสีพื้นหลังของ rtTd เมื่อ component ถูก mount ครั้งแรก
      if (!isMounted.current) {
        rtTdBg.current = rtTd.style.backgroundColor; // เก็บค่าสีพื้นหลังเดิมของ rtTd

        isMounted.current = true;
      }

      // #rtTd.style.backgroundColor = rtTdBg.current; // เซ็ตสีพื้นหลังของ rtTd เป็นค่าเดิม

      isOpenDate.current = false;
      isOpenTime.current = false;
    },
    [props.editing]
  );

  const handleDateRef = useCallback((ref: HTMLDivElement | null) => {
    if (!ref) {
      return;
    }

    const input = ref.querySelector("input");

    if (!input) {
      return;
    }

    // #const rtTd = input.closest(".rt-td") as HTMLDivElement;

    // เซ็ตสีพื้นหลังของ rtTd เป็น BG_COLOR
    // #rtTd.style.backgroundColor = BG_COLOR;

    const handleOpen = (timeout = 0) => {
      handleRef(ref);
      handleClick();

      setTimeout(() => {
        isOpenDate.current = true;
        isOpenTime.current = false;
      }, timeout);
    };

    if (isOpenTime.current) {
      input.click();
      input.focus();

      handleOpen(152);
    }

    input.onfocus = () => {
      handleOpen();
    };
  }, []);

  const handleTimeRef = useCallback((ref: HTMLDivElement | null) => {
    if (!ref) {
      return;
    }

    const input = ref.querySelector("input") as HTMLInputElement;

    const handleOpen = (timeout = 0) => {
      handleRef(ref);
      handleClick();

      setTimeout(() => {
        isOpenTime.current = true;
        isOpenDate.current = false;
      }, timeout);
    };

    if (isOpenDate.current) {
      input.focus();

      handleOpen(152);
    }

    input.onfocus = () => {
      handleOpen();
    };
  }, []);

  const handleClick = useCallback(() => {
    if (isSearchBoxChanged.current) {
      isSearchBoxChanged.current = false;

      return;
    }

    if (
      !(
        tbodyRef.current &&
        inputRef.current &&
        divRef.current &&
        popupRef.current &&
        rtTableRef.current
      )
    ) {
      return;
    }

    isOpen.current = true;

    const tbody = tbodyRef.current;
    const rtTable = rtTableRef.current;
    const popup = popupRef.current;
    const div = divRef.current;

    // ดึงค่าตำแหน่งของตาราง
    const { left: tableLeft, right: tableRight } = rtTable.getBoundingClientRect();
    const { bottom: tableBottom, top: tableTop } = tbody.getBoundingClientRect();
    const { bottom, left, right, top } = inputRef.current.getBoundingClientRect();

    // ปรับขนาดของ div ที่ครอบตัว popup
    div.style.marginBottom = `-${MARGIN}px`;

    // ตรวจสอบและปรับ scroll ของตารางแนวนอน
    if (tableRight < right) {
      rtTable.scrollLeft = rtTable.scrollLeft + (right - tableRight) + MARGIN;
    }

    if (tableLeft > left) {
      rtTable.scrollLeft = rtTable.scrollLeft - (tableLeft - left) - MARGIN;
    }

    // ตรวจสอบและปรับ scroll ของตารางแนวตั้ง
    if (tableBottom < bottom) {
      tbody.scrollTop = tbody.scrollTop + bottom - tableBottom + MARGIN;
    }

    if (tableTop > top) {
      tbody.scrollTop = tbody.scrollTop - (tableTop - top) - MARGIN;
    }

    popup.style.position = "absolute";

    popup.style.zIndex = `${999 - Number(props.index)}`;
    popup.style.width = "fit-content";

    handleScrollTop({ target: tbody } as any);
    handleScrollLeft({ target: rtTableRef.current } as any);
  }, []);

  const handleBlur = useCallback(async (shouldRefresh = true) => {
    if (
      !(
        popupRef.current &&
        divRef.current &&
        tbodyRef.current &&
        rtTableRef.current &&
        shouldRefresh
      )
    ) {
      return;
    }

    const popup = popupRef.current;

    popup.style.marginTop = `${-HEIGHT}px`;
    popup.style.marginLeft = "";
    popup.style.position = "";
    popup.style.zIndex = "";
    popup.style.width = "100%";

    divRef.current.style.marginBottom = "";

    isOpen.current = false;

    inputRef.current?.blur();

    isOpenDate.current = false;
    isOpenTime.current = false;

    setIsRefresh(true);
    setIsRefresh(false);

    isSearchBoxChanged.current = false;

    tbodyRef.current.removeEventListener("scroll", handleScrollTop);
    rtTableRef.current.removeEventListener("scroll", handleScrollLeft);
  }, []);

  const handleDelayBlur = useCallback(() => {
    void handleBlur();

    setTimeout(() => {
      isOpenTime.current = false;
      isOpenDate.current = false;

      isDatetimeChanged.current = false;
    }, 151);
  }, [handleBlur]);

  const handleChange: OnChangeHandler = useCallback(
    (e, data, shouldRefresh = true) => {
      void handleBlur(shouldRefresh);

      props.onChange?.(e, {
        action: data.action,
        data: data.data || null,
        index: props.index,
        name: props.name,
        value: data.value,
      });
    },
    [handleBlur, props.index, props.name,props.onChange]
  );

  const handleChangeDate = useCallback(
    (value: string) => {
      handleChange(null, { value }, value.length === 10 || !value);
    },
    [handleChange]
  );

  const handleChangeSearchBox: SelectedItemHandler = useCallback(
    (value: any, key: any, data: Record<string, any> | null, action) => {
      isSearchBoxChanged.current = true;

      handleChange(null, { action, data, value }, !!value);
    },
    [handleChange]
  );

  const handleChangeDropdown = useCallback(
    (e: SyntheticEvent, data: DropdownProps) => {
      handleChange(e, { value: data.value as number | string });
    },
    [handleChange]
  );

  const handleChangeDropdownOptions = useCallback(
    (e: SyntheticEvent, data: DropdownProps) => {
      handleChange(e, { value: data.value as number | string }, false);
    },
    [handleChange]
  );

  const handleChangeInput = useCallback(
    (e: SyntheticEvent, data: InputProps) => {
      handleChange(e, { value: data.value as string }, false);
    },
    [handleChange]
  );

  const handleChangeTreeview: TreeViewSearchBoxProps["onChange"] = (data) => {
    handleChange(null, { data, value: data.value });
  };

  const handleChangeTime = useCallback(
    (time: string) => {
      const value = props.value?.toString().replace(TIME_PATTERN, time) || "";

      handleChange(null, { value }, false);
    },
    [props.value]
  );

  const handleChangeDatetime = useCallback(
    (date: string) => {
      isOpenDate.current = false;
      isOpenTime.current = false;

      isDatetimeChanged.current = true;

      const time = props.value?.toString().split(" ")[1] || "";

      handleChange(null, { value: `${date} ${time || "00:00"}` }, false);
    },
    [props.value]
  );

  const handleCurrentDate = useCallback(() => {
    handleChange(null, { value: formatDatetime(moment()) });
  }, []);

  // Memo
  const paddingTop = useMemo(() => {
    if (["dropdown", "input"].includes(props.boxType)) {
      return 9;
    } else if (["searchbox"].includes(props.boxType)) {
      return 2;
    }

    return 1;
  }, [props.boxType]);

  const value = useMemo(() => {
    if (props.boxType === "dropdown") {
      const text = props.options?.find((item) => String(item.value) === String(props.value))?.text;

      return text as string;
    }

    return props.value?.toString();
  }, [props.boxType, props.options, props.value]);

  const datetimeValue = useMemo(() => {
    if (props.boxType === "datetime" && props.value) {
      const [date, time] = props.value.toString().split(" ");

      return { date, time };
    }

    return { date: "", time: "" };
  }, [props.boxType, props.value]);

  return (
    // @ts-ignore
    <Form
      size={props.size ?? "small"}
      style={
        props.editing ? { height: `calc(${HEIGHT}px - 2px)`, marginTop: -7, position: "unset" } : {}
      }
    >
      <div
        ref={divRef}
        style={props.editing ? { height: HEIGHT, ...styles.content, paddingTop } : {}}
      >
        {!props.editing && (
          <span ref={handleLabelRef} style={{ padding: "7px 5px" }}>
            {value}
          </span>
        )}
      </div>
      {/*  ---------- Popup ----------  */}
      {props.editing && (
        <div ref={popupRef} style={styles.popup}>
          {/* DateTextBox */}
          {props.boxType === "date" && !isRefresh && (
            <DateTextBox
              disabled={props.disabled}
              inputRef={handleRef}
              inputStyle={styles.dateTextboxInput}
              style={styles.dateTextbox}
              value={props.value || ""}
              inputFluid
              useAdDate
              setLanguage={"en"}
              // callback
              onChange={handleChangeDate}
            />
          )}
          {props.boxType === "datetime" && !isRefresh ? (
            <div className="datetime-text-box">
              <div ref={handleDateRef}>
                <DateTextBox
                  disabled={props.disabled}
                  inputIcon=""
                  inputStyle={styles.dateTimeTextboxInput}
                  value={datetimeValue.date}
                  inputFluid
                  // callback
                  onChange={handleChangeDatetime}
                />
              </div>
              <div className="separator">-</div>
              <div ref={handleTimeRef}>
                <TimeComboBox
                  defaultValue={datetimeValue.time}
                  disabled={props.disabled}
                  icon={"-" as any}
                  inputStyle={styles.icon}
                  placeholder=""
                  fluid
                  onTextChange={handleChangeTime}
                />
              </div>
              <Icon.Group className="right-icon" onClick={handleCurrentDate}>
                <Icon disabled={props.disabled} name="calendar alternate outline" />
                <Icon disabled={props.disabled} name="time" corner />
              </Icon.Group>
            </div>
          ) : null}
          {/* SearchBoxDropdown */}
          {props.boxType === "searchbox" && (
            <div ref={handleRef}>
              <SearchBoxDropdown
                onEvent={props.onEvent as ValueProps<"searchbox">["onEvent"]}
                id={props.id}
                defaultText={props.defaultText}
                disabled={props.disabled}
                dropdownStyle={styles.searchboxDropdown}
                icon="search"
                limit={20}
                selectedItem={Array.isArray(props.value) ? null : props.value || null}
                style={styles.searchbox}
                type={props.type as ValueProps<"searchbox">["type"]}
                valueKey={props.valueKey}
                fluid
                inline
                useWithKey
                searchedItemListWithKey={props.searchedItemListWithKey}
                setSelectedItem={handleChangeSearchBox}
                // callback
                mapOptions={props.mapOptions}
                onBlur={props.onBlur}
                onBlurInput={props.onBlurInput}
              />
            </div>
          )}
          {/* Dropdown */}
          {props.boxType === "dropdown" && (
            <div ref={handleRef} className={props.pointing ? "force-dropdown-pointing" : ""}>
              <Dropdown
                className="inline-label"
                disabled={props.disabled}
                multiple={props.multiple}
                style={props.dropdownStyles ? props.dropdownStyles : styles.dropdown}
                value={props.value || ""}
                fluid
                search
                selection
                icon={props.icon}
                pointing={props.pointing}
                options={props.options}
                placeholder={props.placeholder}
                loading={props.dropdownLoading}
                // callback
                onChange={handleChangeDropdown}
              />
            </div>
          )}
          {/* DropdownOptions */}
          {props.boxType === "dropdown-options" && (
            <div ref={handleRef}>
              <DropdownOptions
                disabled={props.disabled}
                menuStyle={props.menuStyle}
                multiple={props.multiple}
                name={props.name}
                value={props.value as ValueProps<"dropdown-options">["value"]}
                checked
                clearable
                inline
                search
                searchBox
                options={props.options as ValueProps<"dropdown-options">["options"]}
                showSubOptions={props.showSubOptions}
                onChange={handleChangeDropdownOptions}
              />
            </div>
          )}
          {/* Input */}
          {props.boxType === "input" && (
            <div ref={handleRef}>
              <Input
                disabled={props.disabled}
                style={styles.input}
                value={props.value}
                fluid
                onChange={handleChangeInput}
              />
            </div>
          )}
          {/* TreeViewSearchBox */}
          {props.boxType === "treeview" && (
            <div ref={handleRef}>
              <TreeViewSearchBox
                id={props.id}
                disabled={props.disabled}
                items={props.items}
                value={props.value}
                fixPosition
                onChange={handleChangeTreeview}
                onParentSearch={props.onParentSearch}
                onSearch={props.onSearch}
              />
            </div>
          )}
        </div>
      )}
    </Form>
  );
};

EditorBoxColumn.displayName = "EditorBoxColumn";

export default React.memo(EditorBoxColumn) as <T extends BoxTypes>(
  props: EditorBoxColumnProps<T>
) => ReactElement;
